import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Star, Send, Trash2, Loader2, User, Lock, Disc3 } from "lucide-react";
import { usePublicAuth } from "../context/PublicAuthContext";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function StarRating({ value, onChange, readonly }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          disabled={readonly}
          onClick={() => onChange && onChange(n === value ? 0 : n)}
          onMouseEnter={() => !readonly && setHovered(n)}
          onMouseLeave={() => !readonly && setHovered(0)}
          className={`transition-colors ${readonly ? "cursor-default" : "cursor-pointer"}`}
        >
          <Star
            size={14}
            className={
              n <= (hovered || value)
                ? "text-amber-400 fill-amber-400"
                : "text-zinc-700"
            }
          />
        </button>
      ))}
    </div>
  );
}

function CommentItem({ comment, canDelete, onDelete }) {
  const date = new Date(comment.created_at).toLocaleDateString("de-DE", {
    day: "2-digit", month: "2-digit", year: "numeric",
  });

  const avatarUrl = comment.avatar_url && !comment.avatar_url.includes("gravatar.com") ? comment.avatar_url : null;
  const initials = (comment.username || "?")[0].toUpperCase();

  return (
    <div className="flex gap-3 py-3 border-b border-zinc-800/60 last:border-0" data-testid="comment-item">
      {/* Avatar mit Ring + Icon-Badge */}
      <div className={`relative w-8 h-8 rounded-full flex-shrink-0 ring-2 ${comment.is_artist ? "ring-blue-500/50" : comment.is_supporter ? "ring-rose-500/50" : "ring-white/10"}`}>
        {avatarUrl ? (
          <img src={avatarUrl} alt="" className="w-full h-full rounded-full object-cover" />
        ) : (
          <div className="w-full h-full bg-zinc-800 rounded-full flex items-center justify-center">
            <span className="text-[10px] font-bold text-zinc-400">{initials}</span>
          </div>
        )}
        {comment.is_artist && (
          <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-blue-600 rounded-full border-2 border-zinc-950 flex items-center justify-center">
            <Disc3 size={6} className="text-white" />
          </span>
        )}
        {comment.is_supporter && (
          <span className={`absolute -bottom-0.5 ${comment.is_artist ? '-left-0.5' : '-right-0.5'} w-3.5 h-3.5 bg-rose-600 rounded-full border-2 border-zinc-950 flex items-center justify-center`}>
            <Star size={5} className="text-white fill-white" />
          </span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          {comment.profil_public
            ? (
              <Link to={`/user/${encodeURIComponent(comment.username)}`}
                className="text-zinc-300 hover:text-white text-sm font-semibold transition-colors"
                data-testid="comment-user-link">
                {comment.username}
              </Link>
            )
            : <span className="text-zinc-300 text-sm font-semibold">{comment.username}</span>
          }
          {comment.is_artist && (
            <span className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/15 flex-shrink-0">
              <Disc3 size={8} /> Artist
            </span>
          )}
          {comment.is_supporter && (
            <span className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-rose-500/10 text-rose-400 border border-rose-500/15 flex-shrink-0">
              <Star size={8} className="fill-rose-400" /> Supporter
            </span>
          )}
          {comment.rating && <StarRating value={comment.rating} readonly />}
          <span className="text-zinc-600 text-xs ml-auto">{date}</span>
          {canDelete && (
            <button
              onClick={() => onDelete(comment.id)}
              className="text-zinc-700 hover:text-red-500 transition-colors"
              data-testid="delete-comment-btn"
              title="Kommentar löschen"
            >
              <Trash2 size={12} />
            </button>
          )}
        </div>
        <p className="text-zinc-400 text-sm leading-relaxed break-words">{comment.text}</p>
      </div>
    </div>
  );
}

export default function CommentsSection({ releaseId }) {
  const { publicUser, isPublicAuth, login, publicApi } = usePublicAuth();
  const isAdmin = ["developer", "admin"].includes(publicUser?.role);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [rating, setRating] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const isSupporter = publicUser?.is_supporter;

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/comments/${releaseId}`)
      .then((r) => r.json())
      .then((data) => {
        // Normalize _id → id
        setComments(data.map((c) => ({ ...c, id: c.id || c._id })));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [releaseId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSubmitting(true);
    setError("");
    try {
      const res = await publicApi("post", "/comments/", {
        release_id: releaseId,
        text: text.trim(),
        rating: rating || null,
      });
      setComments((prev) => [res.data, ...prev]);
      setText("");
      setRating(0);
    } catch (err) {
      setError(err.response?.data?.detail || "Fehler beim Senden");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (commentId) => {
    try {
      await publicApi("delete", `/comments/${commentId}`);
      setComments((prev) => prev.filter((c) => c.id !== commentId));
    } catch {
      alert("Fehler beim Löschen");
    }
  };

  const avgRating =
    comments.filter((c) => c.rating).length > 0
      ? (
          comments.reduce((s, c) => s + (c.rating || 0), 0) /
          comments.filter((c) => c.rating).length
        ).toFixed(1)
      : null;

  return (
    <div className="mt-8" data-testid="comments-section">
      <div className="flex items-center gap-3 mb-4">
        <h2 className="text-xs font-bold uppercase tracking-widest text-zinc-500">
          Kommentare {comments.length > 0 && `(${comments.length})`}
        </h2>
        {avgRating && (
          <div className="flex items-center gap-1.5 text-zinc-400 text-xs">
            <Star size={11} className="text-amber-400 fill-amber-400" />
            <span>{avgRating} / 5</span>
          </div>
        )}
      </div>

      {/* Kommentar schreiben */}
      {!isPublicAuth ? (
        <div className="bg-zinc-900/50 border border-white/5 rounded-xl p-4 flex items-center gap-3 mb-5">
          <Lock size={14} className="text-zinc-600 flex-shrink-0" />
          <p className="text-zinc-500 text-sm">
            <button onClick={() => login()} className="text-rose-400 hover:text-rose-300 underline mr-1">
              Anmelden
            </button>
            und Supporter werden, um Kommentare zu schreiben.
          </p>
        </div>
      ) : !isSupporter ? (
        <div className="bg-zinc-900/50 border border-white/5 rounded-xl p-4 flex items-center gap-3 mb-5">
          <Star size={14} className="text-rose-400 flex-shrink-0" />
          <p className="text-zinc-500 text-sm">
            <Link to="/supporter" className="text-rose-400 hover:text-rose-300 underline mr-1">
              HUWND Supporter
            </Link>
            werden, um Kommentare zu schreiben.
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="mb-5 space-y-2" data-testid="comment-form">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-zinc-500 text-xs">Bewertung:</span>
            <StarRating value={rating} onChange={setRating} />
            {rating > 0 && (
              <button type="button" onClick={() => setRating(0)} className="text-zinc-700 text-xs hover:text-zinc-500">
                Zurücksetzen
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Dein Kommentar..."
              rows={2}
              maxLength={500}
              data-testid="comment-input"
              className="flex-1 bg-zinc-900 border border-zinc-800 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-rose-600 transition-colors resize-none placeholder:text-zinc-600"
            />
            <button
              type="submit"
              disabled={submitting || !text.trim()}
              data-testid="comment-submit-btn"
              className="px-3 py-2 bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white rounded-lg transition-colors flex-shrink-0 self-end"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            </button>
          </div>
          {error && <p className="text-red-400 text-xs">{error}</p>}
        </form>
      )}

      {/* Kommentar-Liste */}
      {loading ? (
        <div className="flex justify-center py-4">
          <Loader2 className="animate-spin text-zinc-600" size={18} />
        </div>
      ) : comments.length === 0 ? (
        <p className="text-zinc-600 text-sm py-2">Noch keine Kommentare. Sei der Erste!</p>
      ) : (
        <div>
          {comments.map((c) => (
            <CommentItem
              key={c.id}
              comment={c}
              canDelete={isAdmin || (publicUser?.username && c.username === publicUser.username)}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
}
